// The empty-2xx guard, shared by all nine `lib/api/*` modules.
//
// openapi-fetch short-circuits a `204`, a `HEAD`, and any response whose
// `Content-Length` is `0` to `data: undefined` (`dist/index.js:230`). Every
// module here then folds that into `const data = (result.data as any) ?? null`,
// and every module's `return res.data as T` therefore hands a screen a `null`
// wearing the contracted type. TypeScript cannot see it — `as` is a cast, not a
// check — so this shipped for the whole life of the API layer.
//
// It is not a cosmetic defect. `scripts/mobile-invalid-data-audit.mjs` answers
// `GET /api/v1/diaries` with a bodyless `204` — which the OpenAPI contract does
// not allow for that route — and `/diaries` **spun on its skeleton forever**:
// `useInfiniteQuery` resolves the query, then computes `hasNextPage` by running
// `getNextPageParam(lastPage)` on the `null` page; that TypeError is swallowed
// by `notifyManager`'s `try { cb() } catch { … }`, so the observer never publishes
// a settled result, `isLoading` stays `true`, and nothing ever retries. The
// audit's `pageerror` count was **zero** — invisible to every error branch the
// app has.
//
// Fixing it at the components does not work, and this is why: the component is
// not where the contract is broken, and the shape of the null is unknowable from
// there (a null page, a null `items`, a null item — three different bugs, one
// indistinguishable `null`). So the guard goes in the one funnel every response
// passes through, and the funnel takes the caller's intent as its input.
//
// **Why the intent cannot be read off the status.** 204 is the one status that
// *means* "no content", and it would be tempting to exempt it — but `204` on
// `GET /api/v1/diaries` is precisely the bug above, while `204` on `DELETE
// /api/v1/diaries/{id}` is the contract. Same status, opposite meaning, and only
// the route knows which. Every route in this API that declares no body declares
// `'204'` in `docsnew/11-api/openapi.yaml`; every route that declares a body
// declares `'200'`. So the opt-out list below is exactly the set of `Promise<void>`
// wrappers, and `empty-body-contract.test.ts` fails if one of them forgets.
//
// An empty 2xx that reaches a screen any other way is a server that broke its own
// contract, and it has to land on the screen's `isError` branch — which every
// list/detail screen in this app already has, with a retry — instead of
// masquerading as "you have no rows".

import { ApiError } from "./pair";
import { API_RESPONSE_EMPTY, EMPTY_RESPONSE_BODY_CODE } from "../i18n/errors";

/**
 * Throw when a successful response carried no body but the caller was written
 * against a contract that declares one.
 *
 * `expectBody` is the caller's declaration, not an inference: routes whose
 * contract has no 2xx body pass `expectBody: false` via `CallOptions`.
 */
export function assertResponseBody(response: Response, data: unknown, expectBody: boolean): void {
  if (!expectBody) return;
  if (data !== null && data !== undefined) return;
  throw new ApiError(response.status, EMPTY_RESPONSE_BODY_CODE, API_RESPONSE_EMPTY);
}
